import { useMemo } from 'react';
import { useRouter } from 'next/router';

type Route = Readonly<{
    readonly title: string;
    readonly optionalQueries: Record<string, null>;
    readonly queries: Record<string, null>;
}>;

type Routes = Readonly<Record<string, Route>>;

interface UseRoutes<TRoutes extends Routes, TPersistentQueries extends Record<string, null>> {
    back: () => Promise<void>;
    createRoute: <
        TUrl extends keyof TRoutes,
    >(
        url: TUrl,
        queries: Record<keyof TRoutes[TUrl]['queries'], string> & Partial<Record<keyof TRoutes[TUrl]['optionalQueries'], string>> & Partial<Record<keyof TPersistentQueries, string>>,
    ) => string;
    pushRoute: <
        TUrl extends keyof TRoutes,
    >(
        url: TUrl,
        queries: Record<keyof TRoutes[TUrl]['queries'], string> & Partial<Record<keyof TRoutes[TUrl]['optionalQueries'], string>> & Partial<Record<keyof TPersistentQueries, string>>,
    ) => Promise<void>;
    replaceRoute: <
        TUrl extends keyof TRoutes,
    >(
        url: TUrl,
        queries: Record<keyof TRoutes[TUrl]['queries'], string> & Partial<Record<keyof TRoutes[TUrl]['optionalQueries'], string>> & Partial<Record<keyof TPersistentQueries, string>>,
        config?: { raw: boolean }
    ) => Promise<void>;
    replaceRouteRaw: (
        url: string,
    ) => Promise<void>;
    getQueriesForRoute: <
        TUrl extends keyof TRoutes,
    >(
        url: TUrl,
    ) => Record<keyof TRoutes[TUrl]['queries'], string | undefined> & Partial<Record<keyof TRoutes[TUrl]['optionalQueries'], string | undefined>> & Partial<Record<keyof TPersistentQueries, string>>;
    replacePersistentQuery: (values: Partial<Record<keyof TPersistentQueries, string | undefined>>) => Promise<void>;
}

interface RouterConfig {
    persistentQueries: Record<string, null>;
}

export const createAppRouter = <TRoutes extends Readonly<Routes>, TRouterConfig extends RouterConfig>(routes: TRoutes, config: TRouterConfig) => (): UseRoutes<TRoutes, TRouterConfig['persistentQueries']> => {
    const router = useRouter();
    return useMemo(() => {
        const createRoute = <TUrl extends keyof TRoutes,
        >(
            url: TUrl,
            queries: Record<keyof TRoutes[TUrl]['queries'], string> & Partial<Record<keyof TRoutes[TUrl]['optionalQueries'], string>>,
        ): string => {
            const persistentQueries = {} as Record<string, string>;

            for (const key in config.persistentQueries) {
                const query = router.query[key];
                if (typeof query === 'string') {
                    persistentQueries[key] = query;
                }
            }

            const queryParams = Object.entries({
                ...persistentQueries,
                ...queries,
            })
                .map(([ key, value ]) => `${key}=${encodeURIComponent(value as string)}`);

            if (queryParams.length === 0) {
                return `${String(url)}`;
            }

            return `${String(url)}?${queryParams.join('&')}`;
        };

        return ({
            back: async (): Promise<void> => await router.back(),
            createRoute,
            getQueriesForRoute: <
                TUrl extends keyof TRoutes,
            >(
                url: TUrl,
            ): Record<keyof TRoutes[TUrl]['queries'], string> & Partial<Record<keyof TRoutes[TUrl]['optionalQueries'], string>> => {
                const result = {} as Record<any, any>;
                for (const key in routes[url].queries) {
                    result[key] = router.query[key];
                }
                for (const key in routes[url].optionalQueries) {
                    result[key] = router.query[key];
                }
                for (const key in config.persistentQueries) {
                    result[key] = router.query[key];
                }
                return result as any;
            },
            pushRoute: async <TUrl extends keyof TRoutes,
            >(
                url: TUrl,
                queries: Record<keyof TRoutes[TUrl]['queries'], string> & Partial<Record<keyof TRoutes[TUrl]['optionalQueries'], string>>,
            ): Promise<void> => {
                const route = createRoute(url, queries);
                await router.push(route);
            },
            replacePersistentQuery: async (values: Partial<Record<keyof TRouterConfig['persistentQueries'], string | undefined>>) => {
                await router.push({
                    query: {
                        ...Object.fromEntries((new URLSearchParams(window.location.search)).entries()),
                        ...values,
                    },
                }, undefined, { shallow: true });
            },
            replaceRoute: async <TUrl extends keyof TRoutes,
            >(
                url: TUrl,
                queries: Record<keyof TRoutes[TUrl]['queries'], string> & Partial<Record<keyof TRoutes[TUrl]['optionalQueries'], string>>,
            ): Promise<void> => {
                const route = createRoute(url, queries);
                await router.replace(route);
            },
            replaceRouteRaw: async (
                url: string,
            ): Promise<void> => {
                await router.replace(url);
            },
        });
    }, [ router ]);
};
